import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Star, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg, photoSrc } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Panel } from "@/components/admin/Panel";

export const PhotosManager = () => {
  const { reload: reloadSite } = useSite();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef(null);
  const load = () => api.get("/admin/photos").then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, []);
  const done = () => { load(); reloadSite(); };
  const upload = async (files) => {
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post("/admin/photos", fd); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setUploading(false);
    done();
  };
  const drop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const next = [...photos];
    const [m] = next.splice(from, 1);
    next.splice(i, 0, m);
    setPhotos(next);
    await api.put("/admin/photos/order", { ids: next.map((p) => p.id) });
    reloadSite();
  };
  const patch = async (id, body) => { await api.patch(`/admin/photos/${id}`, body); done(); };
  const remove = async (id) => { if (window.confirm("Remove this photo from the site?")) { await api.delete(`/admin/photos/${id}`); done(); } };
  return (
    <Panel title="Photos" desc="Drag to reorder. The first photo leads the gallery and welcome section; the starred photo is the homepage hero." testid="photos-panel">
      <label className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm cursor-pointer" data-testid="photo-upload-label">
        <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photos"}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading} onChange={(e) => upload([...e.target.files])} data-testid="photo-upload-input" />
      </label>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {photos.map((p, i) => (
          <div key={p.id} draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
            className="border border-border rounded-lg overflow-hidden bg-white" data-testid={`photo-card-${i}`}>
            <div className="relative aspect-[4/3]">
              <img src={photoSrc(p.url)} alt={p.caption} className="w-full h-full object-cover" />
              <GripVertical className="absolute top-2 left-2 w-5 h-5 text-white drop-shadow cursor-grab" />
              {p.is_hero && <span className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded-full">Hero</span>}
            </div>
            <div className="p-3 space-y-2">
              <Input defaultValue={p.caption} placeholder="Caption / alt text" onBlur={(e) => e.target.value !== p.caption && patch(p.id, { caption: e.target.value })} data-testid={`photo-caption-${i}`} />
              <div className="flex justify-between text-xs">
                <button onClick={() => patch(p.id, { is_hero: !p.is_hero })} className="flex items-center gap-1" data-testid={`photo-hero-${i}`}><Star className={`w-4 h-4 ${p.is_hero ? "fill-current text-[hsl(var(--sage))]" : ""}`} /> {p.is_hero ? "Hero photo" : "Set as hero"}</button>
                <button onClick={() => remove(p.id)} className="flex items-center gap-1 text-destructive" data-testid={`photo-remove-${i}`}><Trash2 className="w-4 h-4" /> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};
